// @ts-check
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import FilterCombiner from '../../enums/FilterCombiner';

import BusComponent from '../BusComponent';
import TypeDropdown from '../dataFilter/TypeDropdown';
import Filter from './Filter';
import NewCriterion from './NewCriterion';
import AppConfig from '../../appConfig';
import classNames from 'classnames';

/**
 * @typedef State
 * @property {import('../../').PointsFilterField[]} filters
 * @property {import('../../').AppliedPointFilters} appliedFilters
 * @property {import('../../').PointsFilterField[]} addedCriteria
 * @property {string} filterCombinerType
 * @property {boolean} hidden
 * @property {() => void} onClose
 *
 * @typedef Props
 * @property {import('react-intl').intlShape} intl
 * @property {boolean} hidden
 *
 * @extends {BusComponent<Props, State>}
 */
class FilterPanel extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            filters: [],
            appliedFilters: {},
            filterCombinerType: FilterCombiner.MATCH_ALL.type,
            addedCriteria: [],
            pendingAppliedFilters: {},
            areFiltersChanged: false,
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            POINTS_FILTERS_RESPONSE: this.onPointsFiltersResponse,
            CLEAR_FACILITY_FILTER_FROM_PANEL: this.clearFilters,
            APPLIED_FILTERS_RESPONSE: this.onApplyFiltersResponse,
        });
        this.emit('POINTS_FILTERS_REQUEST');
    }

    onApplyFiltersResponse = (eventMap) => {
        if (eventMap.appliedGeoFilters) {
           this.setState({
               appliedFilters: eventMap.appliedGeoFilters.appliedFilters,
           })
            this.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
                filterCombinerType: eventMap.appliedGeoFilters.filterCombinerType,
                appliedFilters: eventMap.appliedGeoFilters.appliedFilters,
            });
        }
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    /**
     *
     * @param {import('../../').PointsFilterField[]} filters
     * @param {import('../../').PointsFilterField[]} receivedAddedCriteria
     * @param {import('../../').AppliedPointFilters} receivedAppliedFilters
     * @property {string} receivedFilterCombinerType
     */
    onPointsFiltersResponse = (filters, receivedAddedCriteria, receivedAppliedFilters, receivedFilterCombinerType) => {
        // Figure out if there are criteria and filters that need to be added by default
        // Those filters should have filterType and value already set
        const appliedFilters = Object.keys(receivedAppliedFilters).length > 0 ? receivedAppliedFilters : { ...this.state.appliedFilters };
        const addedCriteria = receivedAddedCriteria.length > 0 ? receivedAddedCriteria : [...this.state.addedCriteria];
        const filterCombinerType = receivedFilterCombinerType === null ? this.state.filterCombinerType : receivedFilterCombinerType;
        filters
            .filter(filter => filter.value !== undefined && filter.filterType)
            .forEach((filter, idx) => {
                const key = `${filter.property}${idx}`;
                appliedFilters[key] = {
                    label: filter.label,
                    type: filter.type,
                    value: filter.value,
                    filterType: filter.filterType,
                };
                const newFilter = { ...filter, property: key };
                addedCriteria.push(newFilter);
            });
        this.setState({ filters, appliedFilters, addedCriteria, filterCombinerType });
    };

    /**
     *
     * @param {import('../../').PointsFilterField} filter
     * @param {string | number} value
     * @param {import('../../').FilterType} filterType
     */
    addFilter = (filter, value, filterType) => {
        let criteria = [];
        if (this.state.addedCriteria.length > 0 && value !== null) {
            criteria = this.state.addedCriteria;
            const singleCriteria = criteria.find(crit => {
                if (crit.property === filter.property) {
                    return crit;
                }
                return null;
            });
            if (singleCriteria) {
                singleCriteria.value = value;
                singleCriteria.filterType = filterType;
                const index = criteria.indexOf(singleCriteria);
                criteria[index] = singleCriteria;
            }
        }
        this.setState({
            pendingAppliedFilters: {
                ...this.state.pendingAppliedFilters,
                [filter.property]: {
                    label: filter.label,
                    type: filter.type,
                    value,
                    filterType,
                },
            },
            addedCriteria: criteria,
            areFiltersChanged: true,
        });
    };

    /**
     *
     * @param {string} property
     */
    removeFilter = (property) => {
        const pendingAppliedFilters = {
            ...this.state.pendingAppliedFilters,
        };
        delete pendingAppliedFilters[property];

        this.setState({
            pendingAppliedFilters,
            areFiltersChanged: true,
        });
    };

    applyFilters = () => {
        const { pendingAppliedFilters, filterCombinerType, addedCriteria } = this.state;

        this.setState({
            appliedFilters: { ...pendingAppliedFilters },
            areFiltersChanged: false,
        });

        this.emit('APPLY_POINTS_FILTERS_REQUEST', {
            filterCombinerType,
            appliedFilters: pendingAppliedFilters,
            addedCriteria,
        });

        this.emit('UPDATE_APPLIED_FILTERS_REQUEST', {
            appliedGeoFilters: {
                appliedFilters: pendingAppliedFilters,
            }
        });

        this.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
            filterCombinerType,
            appliedFilters: pendingAppliedFilters,
        });
    };

    clearFilters = () => {
        this.setState({
            filters: [],
            appliedFilters: {},
            addedCriteria: [],
            filterCombinerType: FilterCombiner.MATCH_ALL.type
        });
        this.emit('HIDE_FILTER_AND_MASK_INDICATOR');
        this.emit('APPLY_POINTS_FILTERS_REQUEST', {
            appliedFilters: {},
            addedCriteria: [],
            filterCombinerType: FilterCombiner.MATCH_ALL.type,
        });
    };

    /** @param {string} filterCombinerType */
    onCombinerChange = filterCombinerType => {
        this.setState({ filterCombinerType });
    };

    /** @param {object} filter */
    addCriterion = filter => {
        // Make a new property value for the filter. Use current value and append Datetime to it
        const filterProperty = `${filter.property}@${Date.now()}`;
        // add to applied filters
        this.addFilter({ ...filter, property: filterProperty }, filter.value, filter.filterType);
        this.setState({ addedCriteria: [...this.state.addedCriteria, { ...filter, property: filterProperty }] });
    };

    /** @param {object} filter */
    removeCriterion = filter => {
        const addedCriteria = [...this.state.addedCriteria];
        const indexOfCriteriaWithProperty = addedCriteria.findIndex(
            f => f.property === filter.property,
        );
        if (indexOfCriteriaWithProperty !== -1) {
            addedCriteria.splice(indexOfCriteriaWithProperty, 1);
        }
        // Remove from pending filters first
        const pendingAppliedFilters = { ...this.state.pendingAppliedFilters };
        delete pendingAppliedFilters[filter.property];

        this.setState({
            addedCriteria,
            pendingAppliedFilters,
            areFiltersChanged: true,
        });
        if (addedCriteria.length === 0) {
            this.emit('HIDE_FILTER_AND_MASK_INDICATOR');
        }
    };

    render() {
        const { intl, hidden } = this.props;
        const { areFiltersChanged, addedCriteria, appliedFilters, filterCombinerType,  } = this.state;

        const isApplyButtonDisabled = !areFiltersChanged;

        const filters = addedCriteria.map(filter => (
            <div
                key={filter.property}
                className="flex-it center filter-wrapper"
            >
                <Filter
                    filter={filter}
                    addFilter={this.addFilter}
                    removeFilter={this.removeFilter}
                />
                <div className="flex-it center">
                    <div className="divider divider--vertical divider--btn-separator" />
                    <button
                        className="btn-icon btn-icon--small"
                        onClick={() => this.removeCriterion(filter)}
                    >
                        <i className="material-icons">close</i>
                    </button>
                </div>
            </div>
        ));

        return (
            <div
                className={classNames('points-filter-panel', {
                    hidden: hidden,
                })}
            >
                <div className="flex-it center points-filter-panel__header">
                    <FormattedMessage
                        id={`${AppConfig.constants.searchBox.findByCriteriaDescription}`}
                        values={{
                            chunk: (
                                <TypeDropdown
                                    value={filterCombinerType}
                                    onChange={this.onCombinerChange}
                                    types={Object.values(FilterCombiner).map(
                                        type => ({
                                            ...type,
                                            text: intl.formatMessage({ id: type.text }),
                                        }),
                                    )}
                                    dropdownClassName="simple-dropdown--primary-rounded"
                                />
                            ),
                        }}
                    />
                </div>
                <div className="points-filter-panel__filters">
                    {filters}
                </div>
                <div className="flex-it center points-filter-panel__criterion">
                    <NewCriterion
                        addCriterion={this.addCriterion}
                        addedCriteria={addedCriteria}
                    />
                </div>
                <div className="flex-it center flex-end points-filter-panel__footer">
                    {Object.keys(appliedFilters).length > 0 &&
                        <button
                            className="points-filter-panel__clear-filters"
                            onClick={this.clearFilters}
                        >
                            {intl.formatMessage({ id: 'dataBrowser.clearFilters' })}
                        </button>
                    }
                    <button
                        className="btn-raised"
                        disabled={isApplyButtonDisabled}
                        onClick={this.applyFilters}
                    >
                        {intl.formatMessage({ id: `${AppConfig.constants.filterButtonText}` })}
                    </button>
                </div>
            </div>
        );
    }
}

export default injectIntl(FilterPanel);
